import { motion } from 'framer-motion'
import { Badge, Tag } from './ui'
import { useT } from '../lib/i18n'
import type { IndustryLocale } from '../lib/locales'
import {
  INDUSTRY_PARADIGMS,
  type IndustryId,
} from '../data/industryParadigms'

interface HomePageProps {
  onEnterDemo: () => void
  onOpenIndustry: (id: IndustryId) => void
}

export function HomePage({ onEnterDemo, onOpenIndustry }: HomePageProps) {
  return (
    <main>
      <HomeHero />
      <WhatIsOntology />
      <UpgradesSection />
      <IndustriesSection onOpenIndustry={onOpenIndustry} />
      <BottomCTA onEnterDemo={onEnterDemo} />
    </main>
  )
}

function HomeHero() {
  const t = useT()
  return (
    <section className="relative mx-auto max-w-6xl px-6 sm:px-10 pt-20 sm:pt-28 pb-10 sm:pb-14">
      <motion.h1
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="text-[color:var(--color-orion-text)] leading-[1.05] max-w-4xl"
        style={{ fontSize: 'var(--text-h1)', fontWeight: 600 }}
      >
        {t.home.hero.titleLine1}
        <br />
        <span className="text-[color:var(--color-orion-amber)]">{t.home.hero.titleLine2}</span>
      </motion.h1>
    </section>
  )
}

function WhatIsOntology() {
  const t = useT()
  const w = t.home.whatIs
  return (
    <section id="what-is-ontology" className="mx-auto max-w-6xl px-6 sm:px-10 py-16 sm:py-20">
      <div className="mb-6">
        <Tag kind="rule" className="text-[length:var(--text-micro)]">
          {w.sectionTag}
        </Tag>
      </div>
      <h2
        className="text-[color:var(--color-orion-text)] mb-6"
        style={{ fontSize: 'var(--text-h2)' }}
      >
        {w.titleA}
        <span className="text-[color:var(--color-orion-amber)]">{w.titleSpan}</span>
      </h2>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="surface-night border-faint rounded-[var(--radius-lg)] p-5 sm:p-6">
          <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
            {w.oneLineLabel}
          </div>
          <p className="text-[color:var(--color-orion-text)] leading-relaxed" style={{ fontSize: 'var(--text-lead)' }}>
            {w.oneLineBefore}
            <span className="mono text-[color:var(--color-orion-violet)]">{w.oneLineObj}</span>
            {' · '}
            <span className="mono text-[color:var(--color-orion-cyan)]">{w.oneLineRel}</span>
            {' · '}
            <span className="mono text-[color:var(--color-orion-warn)]">{w.oneLineCon}</span>
            {' · '}
            <span className="mono text-[color:var(--color-orion-pass)]">{w.oneLineAct}</span>
            {w.oneLineAfter}
          </p>
        </div>
        <div className="surface-night border-faint rounded-[var(--radius-lg)] p-5 sm:p-6">
          <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
            {w.solvesLabel}
          </div>
          <ul className="space-y-2 text-[color:var(--color-orion-haze)]">
            {w.solvesItems.map((item, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-[color:var(--color-orion-block)] shrink-0">×</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-6 surface-night border-faint border-l-2 border-l-[color:var(--color-orion-amber)] rounded-[var(--radius-lg)] p-5 sm:p-6">
        <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
          {w.ontologyAiLabel}
        </div>
        <p className="text-[color:var(--color-orion-text)]" style={{ fontSize: 'var(--text-lead)', lineHeight: 1.6 }}>
          {w.ontologyAiBody1}
          <span className="text-[color:var(--color-orion-amber)] font-medium">{w.ontologyAiSpan}</span>
          {w.ontologyAiBody2}
        </p>
      </div>
    </section>
  )
}

function UpgradesSection() {
  const t = useT()
  const u = t.home.upgrades
  return (
    <section className="mx-auto max-w-6xl px-6 sm:px-10 py-16 sm:py-20">
      <div className="mb-6">
        <Tag kind="rule" className="text-[length:var(--text-micro)]">
          {u.sectionTag}
        </Tag>
      </div>
      <h2
        className="text-[color:var(--color-orion-text)] mb-8"
        style={{ fontSize: 'var(--text-h2)' }}
      >
        {u.titleA}
        <span className="text-[color:var(--color-orion-amber)]">{u.titleSpan}</span>
        {u.titleB}
      </h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {u.items.map((item, i) => (
          <motion.div
            key={item.title}
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ delay: i * 0.06, duration: 0.4 }}
            className="surface-night border-faint rounded-[var(--radius-lg)] p-5"
          >
            <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
              0{i + 1}
            </div>
            <div className="text-[color:var(--color-orion-text)] font-medium mb-2" style={{ fontSize: 'var(--text-lead)' }}>
              {item.title}
            </div>
            <p className="text-[length:var(--text-small)] text-[color:var(--color-orion-haze)] leading-relaxed">
              {item.body}
            </p>
          </motion.div>
        ))}
      </div>
    </section>
  )
}

function IndustriesSection({
  onOpenIndustry,
}: {
  onOpenIndustry: (id: IndustryId) => void
}) {
  const t = useT()
  const s = t.home.industriesSection
  const paradigmTitleById = new Map(t.paradigms.paradigms.map((p) => [p.id, p.title]))
  return (
    <section id="industries" className="mx-auto max-w-6xl px-6 sm:px-10 py-16 sm:py-20 scroll-mt-20">
      <div className="mb-6">
        <Tag kind="rule" className="text-[length:var(--text-micro)]">
          {s.sectionTag}
        </Tag>
      </div>
      <h2
        className="text-[color:var(--color-orion-text)] mb-2"
        style={{ fontSize: 'var(--text-h2)' }}
      >
        {s.titleA}
        <span className="text-[color:var(--color-orion-amber)]">{s.titleSpan}</span>
      </h2>
      <p
        className="text-[color:var(--color-orion-haze)] max-w-3xl mb-10"
        style={{ fontSize: 'var(--text-lead)' }}
      >
        {s.leadA}
        <span className="text-[color:var(--color-orion-text)]">{s.leadSpan}</span>
        {s.leadB}
      </p>

      <div className="flex flex-col gap-4">
        {t.industries.map((ind, i) => (
          <IndustryCard
            key={ind.id}
            ind={ind}
            index={i}
            labels={s.labels}
            paradigmTitleById={paradigmTitleById}
            industryDemos={t.industryDemos}
            onOpen={onOpenIndustry}
          />
        ))}
      </div>
    </section>
  )
}

function IndustryCard({
  ind,
  index,
  labels,
  paradigmTitleById,
  industryDemos,
  onOpen,
}: {
  ind: IndustryLocale
  index: number
  labels: { applications: string; coreObjects: string; value: string; scenario: string }
  paradigmTitleById: Map<string, string>
  industryDemos?: { [k: string]: { demos?: object } | undefined }
  onOpen: (id: IndustryId) => void
}) {
  const industryId = ind.id as IndustryId
  const paradigms = INDUSTRY_PARADIGMS[industryId]
  const hasDemo =
    industryId === 'energy' ||
    Object.keys(industryDemos?.[industryId]?.demos ?? {}).length > 0
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ delay: (index % 4) * 0.05, duration: 0.4 }}
      className="surface-night border-faint rounded-[var(--radius-lg)] p-6 sm:p-7 hover:border-[color:var(--color-orion-amber)]/40 transition-colors"
    >
      <div className="grid lg:grid-cols-[180px_1fr] gap-6 sm:gap-8">
        <div className="flex lg:flex-col items-start gap-3 lg:gap-3">
          <span className="mono text-[length:var(--text-small)] uppercase tracking-[0.15em] px-3 py-1.5 inline-block w-fit bg-[color:var(--color-orion-amber)]/15 text-[color:var(--color-orion-amber)] border border-[color:var(--color-orion-amber)]/40 rounded-[var(--radius-sm)]">
            {ind.code}
          </span>
          <div>
            <div
              className="text-[color:var(--color-orion-text)] font-medium leading-tight"
              style={{ fontSize: 'var(--text-lead)' }}
            >
              {ind.name}
            </div>
            <div className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)] leading-snug mt-1">
              {ind.nameEn}
            </div>
          </div>
        </div>

        <div className="space-y-5 min-w-0">
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
                {labels.applications}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {ind.applications.map((app) => (
                  <span
                    key={app}
                    className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-text)] px-2 py-0.5 rounded-[var(--radius-xs)] border-faint bg-[color:var(--color-orion-deep)]/40"
                  >
                    {app}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
                {labels.coreObjects}
              </div>
              <p className="text-[length:var(--text-small)] text-[color:var(--color-orion-haze)] leading-relaxed">
                {ind.coreObjects.map((o, i) => (
                  <span key={o}>
                    <span className="mono text-[color:var(--color-orion-violet)]">{o}</span>
                    {i < ind.coreObjects.length - 1 && (
                      <span className="text-[color:var(--color-orion-haze)]/50 mx-1">·</span>
                    )}
                  </span>
                ))}
              </p>
            </div>
          </div>

          <div>
            <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
              {labels.value}
            </div>
            <p
              className="text-[color:var(--color-orion-text)] leading-relaxed"
              style={{ fontSize: 'var(--text-body)' }}
            >
              {ind.benefit}
            </p>
          </div>

          <div className="border-l-2 border-[color:var(--color-orion-amber)]/50 pl-4 py-1">
            <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-1.5">
              {labels.scenario}
            </div>
            <p
              className="text-[color:var(--color-orion-haze)] leading-relaxed"
              style={{ fontSize: 'var(--text-small)' }}
            >
              {ind.scenario}
            </p>
          </div>

          <div className="pt-4 border-t border-[color:var(--color-orion-surface)]/40 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              {hasDemo ? <Badge tone="pass">Live</Badge> : <Badge tone="neutral">Soon</Badge>}
              <span className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)]">
                Paradigms
              </span>
              <div className="flex flex-wrap gap-1.5">
                {paradigms.map((pid) => (
                  <span
                    key={pid}
                    className="text-[length:var(--text-micro)] text-[color:var(--color-orion-text)] px-2 py-0.5 rounded-[var(--radius-xs)] border-faint bg-[color:var(--color-orion-deep)]/40"
                  >
                    {paradigmTitleById.get(pid) ?? pid}
                  </span>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onOpen(industryId)}
              className="mono text-[length:var(--text-micro)] uppercase tracking-widest px-3 py-1.5 rounded-[var(--radius-sm)] border border-[color:var(--color-orion-amber)]/55 bg-[color:var(--color-orion-amber)]/12 text-[color:var(--color-orion-amber)] hover:bg-[color:var(--color-orion-amber)]/22 transition-colors whitespace-nowrap"
            >
              Open →
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

function BottomCTA({ onEnterDemo }: { onEnterDemo: () => void }) {
  const t = useT()
  const c = t.home.bottomCta
  return (
    <section className="mx-auto max-w-6xl px-6 sm:px-10 py-16 sm:py-24">
      <div className="surface-night border-faint rounded-[var(--radius-xl)] p-8 sm:p-12 glow-amber border-[color:var(--color-orion-amber)]/40 flex flex-col items-center text-center">
        <Tag kind="rule" className="text-[length:var(--text-micro)] mb-4">
          {c.sectionTag}
        </Tag>
        <h3
          className="text-[color:var(--color-orion-text)] mb-4 max-w-3xl"
          style={{ fontSize: 'var(--text-h2)' }}
        >
          {c.titleLine1}
          <br />
          <span className="text-[color:var(--color-orion-amber)]">{c.titleLine2}</span>
        </h3>
        <p
          className="text-[color:var(--color-orion-haze)] max-w-2xl mb-6"
          style={{ fontSize: 'var(--text-body)' }}
        >
          {c.body}
        </p>
        <button
          onClick={onEnterDemo}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-[var(--radius-md)] bg-[color:var(--color-orion-amber)] text-[color:var(--color-orion-deep)] font-medium text-[length:var(--text-lead)] hover:bg-[color:var(--color-orion-amber-dim)] transition-colors"
        >
          {c.button}
        </button>
      </div>
    </section>
  )
}
